/**
 * The dock's pin + folder state, as pure functions over one immutable value.
 *
 * `ShellManagerProvider` holds a `Dock` in a single `useState` and every
 * mutator is `setDock(prev => fn(prev, …))` — so this file is where the
 * client-side rules live, and where they can be tested without React:
 *
 *  - a folder with no members does not exist. The repository prunes one in
 *    the same transaction that empties it (moving the last app out, unpinning
 *    the last member); the local state mirrors that so the tile disappears on
 *    the gesture rather than on the next hydrate;
 *  - membership is `ShellPin.folderId` and nothing else — the same shape the
 *    database has, so hydration is a copy, not a join;
 *  - every function returns the SAME reference when nothing changed, which is
 *    what keeps a no-op mutation from re-rendering the rail.
 */
import type { MiniappIconSpec, FolderColorKey } from '@papan/types'
import type { ShellAppKind, ShellFolder, ShellPin } from '../ShellManager.js'

export interface Dock {
  pins: ShellPin[]
  folders: ShellFolder[]
}

export const EMPTY_DOCK: Dock = { pins: [], folders: [] }

const same = (p: { kind: string; ref: string }, kind: string, ref: string) => p.kind === kind && p.ref === ref

/** Next free RAIL slot: the highest already taken, plus one, over the rail's
 *  two occupants — pins NOT in a folder, and folders. Must agree with `pin()`
 *  in shared/database/src/repositories/shell.ts, which computes the same thing;
 *  when they disagree the optimistic tile and the stored row land on different
 *  slots and the tile jumps on the next hydrate.
 *
 *  A pin inside a folder is skipped because its `pinOrder` is a folder-scoped
 *  number, not a rail slot — counting it would push every new pin past a slot
 *  nothing occupies. */
export function nextPinOrder(dock: Dock): number {
  let max = -1
  for (const p of dock.pins) if (p.folderId === null && p.pinOrder > max) max = p.pinOrder
  for (const f of dock.folders) if (f.pinOrder > max) max = f.pinOrder
  return max + 1
}

/** Drop every folder no pin names any more. */
export function pruneEmptyFolders(dock: Dock): Dock {
  const used = new Set<string>()
  for (const p of dock.pins) if (p.folderId) used.add(p.folderId)
  const folders = dock.folders.filter(f => used.has(f.id))
  return folders.length === dock.folders.length ? dock : { ...dock, folders }
}

/** Idempotent pin: appends at the next slot, or refreshes the title of an
 *  existing pin (its slot and folder untouched). */
export function pinInDock(dock: Dock, kind: ShellAppKind, ref: string, title?: string): Dock {
  const existing = dock.pins.find(p => same(p, kind, ref))
  if (existing) {
    if (!title || existing.title === title) return dock
    return { ...dock, pins: dock.pins.map(p => (p === existing ? { ...p, title } : p)) }
  }
  return { ...dock, pins: [...dock.pins, { kind, ref, title, pinOrder: nextPinOrder(dock), folderId: null }] }
}

/** Unpin; prunes the folder this was the last member of. */
export function unpinFromDock(dock: Dock, kind: ShellAppKind, ref: string): Dock {
  const pins = dock.pins.filter(p => !same(p, kind, ref))
  if (pins.length === dock.pins.length) return dock
  return pruneEmptyFolders({ ...dock, pins })
}

/** Mirror of POST /folders. Members that are not pins are ignored — only
 *  pinned apps can be grouped, and the caller pins first. A member pulled out
 *  of another folder may have been its last, so that folder is pruned too. */
export function groupInDock(dock: Dock, folder: ShellFolder, members: Array<{ kind: ShellAppKind; ref: string }>): Dock {
  const wanted = new Set(members.map(m => `${m.kind}:${m.ref}`))
  let touched = false
  const pins = dock.pins.map(p => {
    if (!wanted.has(`${p.kind}:${p.ref}`) || p.folderId === folder.id) return p
    touched = true
    return { ...p, folderId: folder.id }
  })
  if (!touched) return dock
  const folders = dock.folders.some(f => f.id === folder.id) ? dock.folders : [...dock.folders, folder]
  return pruneEmptyFolders({ pins, folders })
}

/** Mirror of PATCH /pins/folder. Moving into a folder this dock does not
 *  know is a no-op (the server would 404 it too); `null` returns the app to
 *  the rail. The folder left behind is pruned if that emptied it. */
export function moveInDock(dock: Dock, kind: ShellAppKind, ref: string, folderId: string | null): Dock {
  if (folderId !== null && !dock.folders.some(f => f.id === folderId)) return dock
  const pin = dock.pins.find(p => same(p, kind, ref))
  if (!pin || pin.folderId === folderId) return dock
  // APPENDED in BOTH directions, exactly as `setPinFolder` does server-side —
  // `pin_order` is a folder row index inside a folder and a rail slot outside
  // one, so carrying the old number across means landing on top of whatever
  // holds it in the other scope. Only the inbound half was mirrored here, so
  // an app leaving a folder rendered at its old folder index (usually first on
  // the rail) while the server appended it — and the tile jumped to the end on
  // the next hydrate, which now actually happens, since `shell:changed` is
  // delivered.
  let pinOrder = pin.pinOrder
  if (folderId !== null) {
    let max = -1
    for (const p of dock.pins) if (p.folderId === folderId && p.pinOrder > max) max = p.pinOrder
    pinOrder = max + 1
  } else {
    pinOrder = nextPinOrder(dock)
  }
  const pins = dock.pins.map(p => (p === pin ? { ...p, folderId, pinOrder } : p))
  return pruneEmptyFolders({ ...dock, pins })
}

export interface FolderPatch { title?: string; color?: FolderColorKey | null }

/** Mirror of PATCH /folders/:id: only the keys present in `patch` change. */
export function updateFolderInDock(dock: Dock, id: string, patch: FolderPatch): Dock {
  const folder = dock.folders.find(f => f.id === id)
  if (!folder) return dock
  const title = patch.title === undefined ? folder.title : patch.title
  const color = patch.color === undefined ? folder.color : patch.color
  if (title === folder.title && color === folder.color) return dock
  return { ...dock, folders: dock.folders.map(f => (f === folder ? { ...f, title, color } : f)) }
}

/** A pinned app was renamed: refresh the title the tile shows. Unlike
 *  `pinInDock` this never pins — an app that is not on the dock stays off it. */
export function setPinTitleInDock(dock: Dock, kind: ShellAppKind, ref: string, title: string): Dock {
  const pin = dock.pins.find(p => same(p, kind, ref))
  if (!pin || pin.title === title) return dock
  return { ...dock, pins: dock.pins.map(p => (p === pin ? { ...p, title } : p)) }
}

/** Mirror of DELETE /folders/:id — the members return to the rail, exactly
 *  what the FK's ON DELETE SET NULL does server-side. */
export function removeFolderFromDock(dock: Dock, id: string): Dock {
  if (!dock.folders.some(f => f.id === id)) return dock
  const folders = dock.folders.filter(f => f.id !== id)
  // The freed members are APPENDED to the rail in their folder order, matching
  // `deleteFolder`. ON DELETE SET NULL renumbers nothing, so mirroring it
  // literally left them holding folder row indices as rail slots — tied with
  // real pins, and resolved by nanoid.
  let next = nextPinOrder({ ...dock, folders })
  const freed = new Map(
    dock.pins.filter(p => p.folderId === id).sort((a, b) => a.pinOrder - b.pinOrder)
      .map(p => [`${p.kind}:${p.ref}`, next++]),
  )
  return {
    pins: dock.pins.map(p => (p.folderId === id
      ? { ...p, folderId: null, pinOrder: freed.get(`${p.kind}:${p.ref}`) ?? p.pinOrder }
      : p)),
    folders,
  }
}

/** Live icon refresh for a pinned app (running instances are handled by the
 *  provider separately). No-op when the ref is not pinned. */
export function setIconInDock(dock: Dock, kind: ShellAppKind, ref: string, icon: MiniappIconSpec | null, iconRev: string | null): Dock {
  const pin = dock.pins.find(p => same(p, kind, ref))
  if (!pin || (pin.icon === icon && (pin.iconRev ?? null) === iconRev)) return dock
  return { ...dock, pins: dock.pins.map(p => (p === pin ? { ...p, icon, iconRev } : p)) }
}

/** A rail key, as the TaskBar and the server both spell it. */
export const railKeyOfPin = (p: { kind: string; ref: string }) => `app:${p.kind}:${p.ref}`
export const railKeyOfFolder = (f: { id: string }) => `folder:${f.id}`

/** Mirror of PUT /rail: every rail pin and folder named in `keys` takes
 *  `pinOrder = its index`. Pins inside a folder and keys naming nothing are
 *  left alone, exactly as the repository does. Same reference back when no
 *  slot changes. */
export function reorderDock(dock: Dock, keys: string[]): Dock {
  const slot = new Map(keys.map((k, i) => [k, i] as const))
  let touched = false
  const pins = dock.pins.map(p => {
    if (p.folderId !== null) return p
    const i = slot.get(railKeyOfPin(p))
    if (i === undefined || i === p.pinOrder) return p
    touched = true
    return { ...p, pinOrder: i }
  })
  const folders = dock.folders.map(f => {
    const i = slot.get(railKeyOfFolder(f))
    if (i === undefined || i === f.pinOrder) return f
    touched = true
    return { ...f, pinOrder: i }
  })
  return touched ? { pins, folders } : dock
}

/** Mirror of PUT /folders/:id/order: the folder's members named in `keys`
 *  take `pinOrder = index`; pins elsewhere and unknown keys are left alone. */
export function reorderFolderInDock(dock: Dock, folderId: string, keys: string[]): Dock {
  const slot = new Map(keys.map((k, i) => [k, i] as const))
  let touched = false
  const pins = dock.pins.map(p => {
    if (p.folderId !== folderId) return p
    const i = slot.get(railKeyOfPin(p))
    if (i === undefined || i === p.pinOrder) return p
    touched = true
    return { ...p, pinOrder: i }
  })
  return touched ? { ...dock, pins } : dock
}
